import type { ChatMessage, ChatMessageSource } from '../../types/chatMessage';
import type { ChatbotMessageWithIdentity } from '../../types/chatbot';

const VISITOR_AUTHOR_ID = 'visitor';

export { VISITOR_AUTHOR_ID };

export function normalizeMessageSources(
  sources: ChatbotMessageWithIdentity['sources'],
  fallbackKnowledgeBaseId?: string,
): ChatMessageSource[] | undefined {
  if (!sources || sources.length === 0) {
    return undefined;
  }

  const fallback = fallbackKnowledgeBaseId?.trim() ?? '';

  return sources.map((source) => ({
    id: source.id ?? undefined,
    documentId: source.documentId ?? undefined,
    documentName: source.documentName ?? undefined,
    knowledgeBaseId: source.knowledgeBaseId?.trim() || fallback || undefined,
    knowledgeBaseName: source.knowledgeBaseName ?? undefined,
    sourceType: source.sourceType ?? undefined,
    sourceUrl: source.sourceUrl ?? undefined,
    sourceTitle: source.sourceTitle ?? undefined,
    trainingExampleId: source.trainingExampleId ?? undefined,
    refStatus: source.refStatus ?? undefined,
  }));
}

type MapChatbotConversationMessagesArgs = {
  rawMessages: ChatbotMessageWithIdentity[];
  conversationId: string;
  visitorName: string;
  chatbotDisplayName: string;
  chatbotKnowledgeBaseId: string;
};

export function mapChatbotConversationMessages({
  rawMessages,
  conversationId,
  visitorName,
  chatbotDisplayName,
  chatbotKnowledgeBaseId,
}: MapChatbotConversationMessagesArgs): ChatMessage[] {
  return rawMessages.map((message) => {
    const role = (message.role ?? '').toUpperCase();
    const isUser = role === 'USER';
    const sentAt =
      message.created ?? message.stamp ?? new Date(0).toISOString();

    return {
      id: message.id,
      sessionId: conversationId,
      content: message.content?.trim() ?? '',
      sentAt,
      author: isUser
        ? {
            type: 'user',
            id: VISITOR_AUTHOR_ID,
            name: visitorName,
          }
        : {
            type: 'ai',
            name: chatbotDisplayName,
          },
      sources: isUser
        ? undefined
        : normalizeMessageSources(
            message.sources,
            chatbotKnowledgeBaseId || undefined,
          ),
    };
  });
}
